import { useCallback, useEffect, useState } from 'react';
import { useAuth, useZudoku } from 'zudoku/hooks';

/**
 * Hook to get the user's access token from the Zudoku context.
 * Only attempts to fetch a token when the user is authenticated and the
 * auth state is not in a pending/hydrating state. Otherwise returns a
 * stable null token with no error — callers can branch on isAuthenticated.
 */
export function useAccessToken() {
  const zudokuContext = useZudoku();
  const { isAuthenticated, isPending } = useAuth();
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const getToken = useCallback(
    async (isCancelled: () => boolean = () => false) => {
      if (!zudokuContext?.authentication) {
        if (isCancelled()) return;
        setError('Authentication context not available');
        setAccessToken(null);
        setIsLoading(false);
        return;
      }
      if (isCancelled()) return;
      setIsLoading(true);
      setError(null);
      try {
        const token = await zudokuContext.authentication.getAccessToken();
        if (!token) {
          throw new Error('No access token available');
        }
        if (isCancelled()) return;
        setAccessToken(token);
        setIsLoading(false);
      } catch (err) {
        if (isCancelled()) return;
        const errorMessage = err instanceof Error ? err.message : 'Failed to get access token';
        setError(errorMessage);
        setAccessToken(null);
        setIsLoading(false);
      }
    },
    [zudokuContext],
  );

  useEffect(() => {
    if (isPending) return;
    if (!isAuthenticated) {
      setAccessToken(null);
      setError(null);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    void getToken(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [isPending, isAuthenticated, getToken]);

  const refreshToken = useCallback(() => {
    if (!isAuthenticated) return Promise.resolve();
    return getToken();
  }, [getToken, isAuthenticated]);

  return {
    accessToken,
    isLoading,
    error,
    refreshToken,
    isAuthenticated: !!accessToken && !error && isAuthenticated,
  };
}

/**
 * Synchronous token accessor for call sites that already know the token
 * should be loaded. Throws if the authentication plugin is not installed.
 */
export function useAccessTokenSync() {
  const zudokuContext = useZudoku();

  const getTokenSync = useCallback(() => {
    if (!zudokuContext?.authentication) {
      throw new Error('Authentication context not available');
    }
    return zudokuContext.authentication.getAccessToken();
  }, [zudokuContext]);

  return {
    getTokenSync,
    isContextAvailable: !!zudokuContext?.authentication,
  };
}
